import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Avatar } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useCompany } from "@/config";
import { MagnifyingGlass, UsersThree, FilePdf, DownloadSimple, EnvelopeSimple, Buildings, IdentificationBadge } from "@phosphor-icons/react";
import jsPDF from "jspdf";

const hexToRgb = (hex) => {
  const h = (hex || "#000000").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const loadDataUrl = async (url) => {
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch (_) { return null; }
};

export default function Directory() {
  const [tree, setTree] = useState(null);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [peek, setPeek] = useState(null);
  const company = useCompany();

  useEffect(() => {
    api.get("/org/hierarchy").then((r) => setTree(r.data)).catch((e) => toast.error(formatErr(e)));
  }, []);

  const rows = useMemo(() => {
    if (!tree) return [];
    const out = [];
    (tree.departments || []).forEach((d) => {
      (d.teams || []).forEach((t) => {
        if (t.admin) {
          out.push({ id: t.admin.id, name: t.admin.name, email: t.admin.email, designation: "Team Admin",
            role: "admin", team: t.name, department: d.name, status: "active" });
        }
        (t.employees || []).forEach((e) => {
          out.push({ id: e.id, name: e.name, email: e.email, designation: e.designation || "—",
            role: "employee", team: t.name, department: d.name, status: e.status || "active" });
        });
      });
    });
    // De-dupe (an admin leading multiple teams would otherwise repeat).
    const seen = new Set();
    return out.filter((r) => { const k = r.id + "|" + r.team; if (seen.has(k)) return false; seen.add(k); return true; });
  }, [tree]);

  const depts = useMemo(() => Array.from(new Set(rows.map((r) => r.department).filter(Boolean))), [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (dept !== "all" && r.department !== dept) return false;
      if (!needle) return true;
      return [r.name, r.email, r.designation, r.team, r.department]
        .filter(Boolean).some((v) => v.toLowerCase().includes(needle));
    });
  }, [rows, q, dept]);

  if (!tree) return <div>Loading…</div>;

  const slug = (company?.shortName || "org").toLowerCase().replace(/\s+/g, "-");
  const stamp = new Date().toISOString().slice(0, 10);

  const exportCSV = () => {
    const headers = ["Name", "Designation", "Role", "Team", "Department", "Email", "Status"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")].concat(
      filtered.map((r) => [r.name, r.designation, r.role === "admin" ? "Team Admin" : "Employee",
        r.team, r.department, r.email, r.status].map(esc).join(",")));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${slug}-directory-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${filtered.length} to CSV`);
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 32;
      const headerH = 56;
      const [sr, sg, sb] = hexToRgb(company?.brand?.secondaryColor || "#1F4E8C");
      const [pr, pg, pb] = hexToRgb(company?.brand?.primaryColor || "#FF5500");
      const logo = await loadDataUrl(company?.brand?.logo);
      const compName = company?.name || company?.shortName || "Organization";
      const cols = [
        { label: "Name", x: margin + 6 },
        { label: "Designation", x: margin + 210 },
        { label: "Team", x: margin + 400 },
        { label: "Department", x: margin + 540 },
        { label: "Email", x: margin + 660 },
      ];
      const drawHeader = (subtitle) => {
        pdf.setFillColor(sr, sg, sb);
        pdf.rect(0, 0, pw, headerH, "F");
        pdf.setFillColor(pr, pg, pb);
        pdf.rect(0, headerH, pw, 4, "F");
        let tx = margin;
        if (logo) { try { pdf.addImage(logo, "PNG", margin, 12, 32, 32); tx = margin + 44; } catch (_) {} }
        pdf.setTextColor(255, 255, 255); pdf.setFont(undefined, "bold"); pdf.setFontSize(15);
        pdf.text(compName, tx, 26);
        pdf.setFont(undefined, "normal"); pdf.setFontSize(9); pdf.setTextColor(222, 226, 232);
        pdf.text(subtitle, tx, 42);
        // column headers
        pdf.setFillColor(243, 243, 241); pdf.rect(margin, headerH + 14, pw - margin * 2, 18, "F");
        pdf.setTextColor(80, 80, 80); pdf.setFont(undefined, "bold"); pdf.setFontSize(8.5);
        cols.forEach((c) => pdf.text(c.label.toUpperCase(), c.x, headerH + 26));
      };
      const sub = `Employee Directory · ${filtered.length} people${dept !== "all" ? " · " + dept : ""} · ${new Date().toLocaleString()}`;
      drawHeader(sub);
      let y = headerH + 48;
      const lh = 16;
      pdf.setFont(undefined, "normal");
      filtered.forEach((r) => {
        if (y > ph - margin) { pdf.addPage(); drawHeader(sub); y = headerH + 48; }
        pdf.setTextColor(30, 30, 30); pdf.setFontSize(9);
        const nm = r.name + (r.role === "admin" ? "  [Admin]" : "") + (r.status && r.status !== "active" ? "  (inactive)" : "");
        pdf.text(pdf.splitTextToSize(nm, 195)[0], cols[0].x, y);
        pdf.setTextColor(80, 80, 80);
        pdf.text(pdf.splitTextToSize(r.designation || "", 180)[0], cols[1].x, y);
        pdf.text(pdf.splitTextToSize(r.team || "", 130)[0], cols[2].x, y);
        pdf.text(pdf.splitTextToSize(r.department || "", 115)[0], cols[3].x, y);
        pdf.text(pdf.splitTextToSize(r.email || "", pw - margin - cols[4].x)[0], cols[4].x, y);
        pdf.setDrawColor(240, 240, 238); pdf.line(margin, y + 4, pw - margin, y + 4);
        y += lh;
      });
      pdf.save(`${slug}-directory-${stamp}.pdf`);
      toast.success(`Exported ${filtered.length} to PDF`);
    } catch (e) {
      toast.error("Export failed");
    }
    setExporting(false);
  };

  return (
    <div data-testid="directory-page">
      <PageHeader overline="Organization" title="Employee Directory"
        subtitle="Search everyone across teams and departments" />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[260px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, designation, team…"
            className="rounded-none border-[#E5E5E5] pl-9" data-testid="directory-search" />
        </div>
        <select value={dept} onChange={(e) => setDept(e.target.value)}
          className="h-10 border border-[#E5E5E5] px-2 bg-white text-sm" data-testid="directory-dept-filter">
          <option value="all">All departments</option>
          {depts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <div className="flex items-center gap-1.5 text-sm text-neutral-500" data-testid="directory-count">
          <UsersThree size={16} /> {filtered.length} of {rows.length}
        </div>
        <div className="flex gap-2 ml-auto">
          <Button onClick={exportCSV} disabled={filtered.length === 0} data-testid="directory-export-csv"
            className="rounded-none bg-white border border-[#E5E5E5] text-neutral-700 hover:bg-neutral-50 gap-2">
            <DownloadSimple size={16} /> CSV
          </Button>
          <Button onClick={exportPDF} disabled={exporting || filtered.length === 0} data-testid="directory-export-pdf"
            className="rounded-none bg-[#1F4E8C] hover:bg-[#163a68] text-white gap-2">
            <FilePdf size={16} weight="bold" /> {exporting ? "…" : "PDF"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        {filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-neutral-500 text-sm">No people match your search.</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b border-[#E5E5E5] bg-[#F7F7F5]">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Designation</th>
              <th className="px-4 py-3">Team</th><th className="px-4 py-3">Department</th>
              <th className="px-4 py-3">Email</th>
            </tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id + "|" + r.team} onClick={() => setPeek(r)}
                  className="border-b border-[#F0F0EE] cursor-pointer hover:bg-[#F7F7F5] transition-colors" data-testid={`directory-row-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={r.name} size={32} />
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {r.name}
                          {r.role === "admin" && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#1F4E8C] text-white">Admin</span>}
                          {r.status && r.status !== "active" && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-neutral-200 text-neutral-600">Inactive</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">{r.designation}</td>
                  <td className="px-4 py-2.5">{r.team}</td>
                  <td className="px-4 py-2.5">{r.department}</td>
                  <td className="px-4 py-2.5 text-neutral-500">{r.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!peek} onOpenChange={(v) => { if (!v) setPeek(null); }}>
        <DialogContent className="rounded-none max-w-md p-0 overflow-hidden" data-testid="profile-peek-dialog">
          {peek && (
            <>
              <div className="bg-[#1F4E8C] px-6 py-5 flex items-center gap-4">
                <Avatar name={peek.name} size={56} className="ring-2 ring-white/30" />
                <div className="min-w-0">
                  <div className="font-head font-semibold text-white text-lg truncate flex items-center gap-2">
                    {peek.name}
                    {peek.role === "admin" && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-white/20 text-white">Admin</span>}
                  </div>
                  <div className="text-white/80 text-sm truncate">{peek.designation || "—"}</div>
                </div>
              </div>
              <div className="px-6 py-4 space-y-3">
                <PeekRow icon={<IdentificationBadge size={16} />} label="Team" value={peek.team} />
                <PeekRow icon={<Buildings size={16} />} label="Department" value={peek.department} />
                <PeekRow icon={<UsersThree size={16} />} label="Role" value={peek.role === "admin" ? "Team Admin" : "Employee"} />
                <PeekRow icon={<EnvelopeSimple size={16} />} label="Email"
                  value={peek.email ? <a href={`mailto:${peek.email}`} className="text-[#1F4E8C] underline underline-offset-2">{peek.email}</a> : "—"} />
                <div className="pt-1">
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 ${peek.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>
                    {peek.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
              {peek.email && (
                <div className="px-6 pb-5">
                  <a href={`mailto:${peek.email}`} data-testid="peek-email-btn">
                    <Button className="w-full rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white gap-2">
                      <EnvelopeSimple size={16} /> Email {peek.name.split(" ")[0]}
                    </Button>
                  </a>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PeekRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="text-neutral-400 mt-0.5">{icon}</span>
      <div className="flex-1">
        <div className="eyebrow text-neutral-400">{label}</div>
        <div className="text-neutral-800">{value || "—"}</div>
      </div>
    </div>
  );
}
